<template>
  <VaDropdown
    v-model="dropdownOpen"
    :offset="[13, 0]"
    class="notification-dropdown"
    stick-to-edges
    :close-on-content-click="false"
  >
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary" class="notification-dropdown">
        <VaBadge v-if="pendingCount > 0" overlap>
          <template #text>{{ pendingCount > 9 ? '9+' : pendingCount }}</template>
          <VaIconNotification class="notification-dropdown__icon" :size="50" color="#4B5563" />
        </VaBadge>
        <VaIconNotification v-else class="notification-dropdown__icon" :size="50" color="#4B5563" />
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <section class="sm:max-h-[360px] p-3 overflow-auto">
        <div v-if="!isAuthenticated" class="text-secondary text-sm px-1 py-2">
          {{ t('notifications.notSignedIn') }}
        </div>
        <div v-else-if="loading" class="flex justify-center py-8">
          <VaProgressCircle indeterminate size="small" />
        </div>
        <div
          v-else-if="!pendingInvites.length && !staffItems.length"
          class="text-secondary text-sm px-1 py-3 text-center"
        >
          {{ t('notifications.noNotifications') }}
        </div>
        <VaList v-else class="space-y-2 mb-0">
          <template v-if="staffItems.length">
            <p class="text-xs font-semibold text-secondary uppercase tracking-wide m-0 px-1">
              {{ t('notifications.teamStaffUpdates') }}
            </p>
            <div
              v-for="s in staffItems"
              :key="'staff-' + s.id"
              class="notification-invite rounded-lg border border-[color:var(--va-background-border)] p-3 bg-[#f8fafc]"
            >
              <div class="flex gap-2 items-start">
                <VaIcon name="groups" color="primary" class="shrink-0 mt-0.5" size="20px" />
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold m-0 leading-snug text-[#1a1a1a]">
                    {{ s.title }}
                  </p>
                  <p class="text-xs text-secondary m-0 mt-2 whitespace-pre-wrap leading-relaxed">
                    {{ s.body }}
                  </p>
                </div>
                <span class="text-xs text-secondary shrink-0 whitespace-nowrap">
                  {{ formatRelative(s.created_at) }}
                </span>
              </div>
            </div>
          </template>
          <div
            v-for="inv in pendingInvites"
            :key="inv.id"
            class="notification-invite rounded-lg border border-[color:var(--va-background-border)] p-3 bg-[#fbfcfe]"
          >
            <div class="flex gap-2 items-start">
              <VaIcon name="group_add" color="primary" class="shrink-0 mt-0.5" size="20px" />
              <div class="flex-1 min-w-0">
                <p class="text-sm font-semibold m-0 leading-snug text-[#1a1a1a]">
                  {{ t('notifications.teamInviteMessage', { team: inv.team_name }) }}
                </p>
                <p v-if="inv.invited_by_name" class="text-xs text-secondary m-0 mt-1">
                  {{ t('notifications.teamInviteFrom', { name: inv.invited_by_name }) }}
                </p>
                <p class="text-xs text-secondary m-0 mt-1 capitalize">
                  {{ t('notifications.teamRole') }}: {{ inv.role }}
                </p>
                <div class="flex flex-wrap gap-2 mt-2">
                  <VaButton
                    size="small"
                    :loading="actionInviteId === inv.id && actionKind === 'accept'"
                    :disabled="actionInviteId === inv.id"
                    @click="onAccept(inv)"
                  >
                    {{ t('notifications.inviteAccept') }}
                  </VaButton>
                  <VaButton
                    size="small"
                    preset="secondary"
                    :loading="actionInviteId === inv.id && actionKind === 'decline'"
                    :disabled="actionInviteId === inv.id"
                    @click="onDecline(inv)"
                  >
                    {{ t('notifications.inviteDecline') }}
                  </VaButton>
                </div>
              </div>
              <span class="text-xs text-secondary shrink-0 whitespace-nowrap">
                {{ formatRelative(inv.created_at) }}
              </span>
            </div>
          </div>
        </VaList>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useToast } from 'vuestic-ui'
import { useAuthStore } from '../../../../stores/auth-store'
import { useTeamsStore } from '../../../../stores/teams'
import { teamsApi, type MyInvite, type StaffInboxItem } from '../../../../api/teams.api'
import VaIconNotification from '../../../icons/VaIconNotification.vue'

const { t, locale } = useI18n()
const { init: notify } = useToast()
const authStore = useAuthStore()
const teamsStore = useTeamsStore()
const { isAuthenticated } = storeToRefs(authStore)

const pendingInvites = ref<MyInvite[]>([])
const staffItems = ref<StaffInboxItem[]>([])
const staffInboxUnread = ref(0)
const loading = ref(false)
const dropdownOpen = ref(false)
const actionInviteId = ref<number | null>(null)
const actionKind = ref<'accept' | 'decline' | null>(null)

const pendingCount = computed(() => pendingInvites.value.length + staffInboxUnread.value)

const TIME_NAMES = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
} as const

const getTimeName = (differenceTime: number) => {
  return Object.keys(TIME_NAMES).reduce(
    (acc, key) => (TIME_NAMES[key as keyof typeof TIME_NAMES] < differenceTime ? key : acc),
    'month',
  ) as keyof typeof TIME_NAMES
}

function formatRelative(iso: string) {
  const d = new Date(iso)
  const rtf = new Intl.RelativeTimeFormat(locale.value, { style: 'short' })
  const timeDifference = Math.round(new Date().getTime() - d.getTime())
  const timeName = getTimeName(timeDifference)
  return rtf.format(-1 * Math.round(timeDifference / TIME_NAMES[timeName]), timeName)
}

async function fetchNotificationPayload() {
  return Promise.all([teamsApi.myInvites(), teamsApi.staffInbox().catch(() => ({ items: [], unread_count: 0 }))])
}

async function refreshNotificationBadge() {
  if (!isAuthenticated.value) {
    pendingInvites.value = []
    staffInboxUnread.value = 0
    return
  }
  try {
    const [invites, inbox] = await fetchNotificationPayload()
    pendingInvites.value = invites
    staffInboxUnread.value = inbox.unread_count
  } catch {
    pendingInvites.value = []
    staffInboxUnread.value = 0
  }
}

async function loadPanelOnOpen() {
  if (!isAuthenticated.value) {
    return
  }
  loading.value = true
  try {
    const [invites, inbox] = await fetchNotificationPayload()
    pendingInvites.value = invites
    staffItems.value = inbox.items
    staffInboxUnread.value = inbox.unread_count
    if (inbox.unread_count > 0) {
      try {
        await teamsApi.markStaffInboxRead()
        staffInboxUnread.value = 0
      } catch {
        /* keep unread badge */
      }
    }
  } catch {
    staffItems.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void refreshNotificationBadge()
})

watch(dropdownOpen, (open) => {
  if (open) void loadPanelOnOpen()
})

watch(isAuthenticated, (ok) => {
  if (ok) void refreshNotificationBadge()
  else {
    pendingInvites.value = []
    staffItems.value = []
    staffInboxUnread.value = 0
    dropdownOpen.value = false
  }
})

async function onAccept(inv: MyInvite) {
  actionInviteId.value = inv.id
  actionKind.value = 'accept'
  try {
    await teamsApi.acceptInvite(inv.id)
    notify({ message: t('notifications.inviteAccepted', { team: inv.team_name }), color: 'success' })
    void teamsStore.fetchTeams()
    await refreshNotificationBadge()
    if (dropdownOpen.value) await loadPanelOnOpen()
  } catch (e: any) {
    const msg = e?.response?.data?.detail
    notify({
      message: typeof msg === 'string' ? msg : t('notifications.inviteActionError'),
      color: 'danger',
    })
  } finally {
    actionInviteId.value = null
    actionKind.value = null
  }
}

async function onDecline(inv: MyInvite) {
  actionInviteId.value = inv.id
  actionKind.value = 'decline'
  try {
    await teamsApi.declineInvite(inv.id)
    notify({ message: t('notifications.inviteDeclined'), color: 'success' })
    await refreshNotificationBadge()
    if (dropdownOpen.value) await loadPanelOnOpen()
  } catch (e: any) {
    const msg = e?.response?.data?.detail
    notify({
      message: typeof msg === 'string' ? msg : t('notifications.inviteActionError'),
      color: 'danger',
    })
  } finally {
    actionInviteId.value = null
    actionKind.value = null
  }
}
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;
  padding: 0px !important;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}
</style>
